<birdz-card [isLoading]="isLoading">
  <div card-title>
    {{ query?.name ?? '' }}
    @if (query && !query.is_public) {
      <mat-icon class="private-icon" [matTooltip]="'QUERY_PRIVATE_TOOLTIP' | transloco">lock_person</mat-icon>
    }
    @if (isDeleted) {
      <mat-chip class="chip-deleted" disableRipple>{{ 'QUERY_DELETED_CHIP' | transloco }}</mat-chip>
    }
  </div>
  <div card-subtitle>{{ query ? query.creator_email + ' — ' + query.database_id : '' }}</div>
  <div card-corner>
    @if (query && isEditor) {
      <button
        mat-icon-button
        [routerLink]="['/query', query.id, 'edit']"
        [disabled]="query.is_locked && query.creator_email !== currentUserEmail"
        [matTooltip]="
          query.is_locked && query.creator_email !== currentUserEmail
            ? ('QUERY_LOCKED_TOOLTIP' | transloco)
            : ('QUERY_EDIT' | transloco)
        "
      >
        <mat-icon>edit</mat-icon>
      </button>
      @if (!isDeleted) {
        <button mat-icon-button [matTooltip]="'QUERY_DUPLICATE' | transloco" (click)="openDuplicateModal()">
          <mat-icon>content_copy</mat-icon>
        </button>
      }
    }
    @if (query) {
      <button
        mat-icon-button
        [class.favorite-active]="query.is_favorite"
        [matTooltip]="'QUERY_FAVORITE' | transloco"
        (click)="toggleFavorite()"
      >
        <mat-icon>{{ query.is_favorite ? 'star' : 'star_border' }}</mat-icon>
      </button>
    }
  </div>
  <div card-body class="execute-body">
    @if (query) {
      <!-- Description (collapsible) -->
      @if (query.description) {
        <div class="query-info" [class.query-info--open]="isDescriptionExpanded">
          <button class="description-toggle" (click)="isDescriptionExpanded = !isDescriptionExpanded">
            <mat-icon>{{ isDescriptionExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
            <span>Description</span>
          </button>
          @if (isDescriptionExpanded) {
            <p class="query-description">{{ query.description }}</p>
          }
        </div>
      }

      <!-- Template fields -->
      @if (templateFields.length > 0) {
        <div class="fields-panel">
          <div class="fields-panel__header">
            <mat-icon>tune</mat-icon>
            <span>{{ 'QUERY_FIELDS_HINT' | transloco }}</span>
          </div>
          <div class="fields-panel__body" [formGroup]="templateForm">
            @for (field of templateFields; track field.name) {
              <div class="field-wrapper">
                @if (field.type === 'date') {
                  <mat-form-field appearance="outline" class="date-field">
                    <mat-label>{{ field.label ?? field.name }}</mat-label>
                    <input matInput [matDatepicker]="datepicker" [formControlName]="field.name" readonly />
                    <mat-datepicker-toggle matIconSuffix [for]="datepicker"></mat-datepicker-toggle>
                    <mat-datepicker #datepicker></mat-datepicker>
                  </mat-form-field>
                } @else {
                  <mat-form-field appearance="outline">
                    <mat-label>{{ field.label ?? field.name }}</mat-label>
                    @if (field.type === 'textlist') {
                      <textarea
                        matInput
                        [formControlName]="field.name"
                        rows="3"
                        placeholder="Une valeur par ligne"
                      ></textarea>
                    } @else if (field.type === 'numberlist') {
                      <textarea
                        matInput
                        [formControlName]="field.name"
                        rows="3"
                        placeholder="Un nombre par ligne"
                      ></textarea>
                    } @else if (field.type === 'number') {
                      <input matInput type="number" [formControlName]="field.name" />
                    } @else if (field.type === 'env') {
                      <mat-select [formControlName]="field.name">
                        @for (val of field.values ?? []; track val) {
                          <mat-option [value]="val">{{ val }}</mat-option>
                        }
                      </mat-select>
                    } @else {
                      <input matInput [formControlName]="field.name" />
                    }
                  </mat-form-field>
                }
                @if (field.description) {
                  <button mat-icon-button type="button" [matTooltip]="field.description" matTooltipClass="help-tooltip">
                    <mat-icon>help_outline</mat-icon>
                  </button>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- Action bar -->
      <div class="action-bar">
        <div class="action-bar__estimate">
          @if (isEstimating) {
            <mat-spinner diameter="16"></mat-spinner>
            <span class="estimate-text">Estimation en cours…</span>
          } @else if (estimatedBytes !== null) {
            <mat-icon class="estimate-icon">query_stats</mat-icon>
            <span class="estimate-text">{{
              'QUERY_ESTIMATE_RESULT' | transloco: { bytes: formattedEstimatedBytes }
            }}</span>
          }
        </div>
        <div class="action-bar__actions">
          <span [matTooltip]="isDeleted ? ('QUERY_DELETED_ACTION_TOOLTIP' | transloco) : ''">
            <button mat-stroked-button [disabled]="isExportingCsv || !query || isDeleted" (click)="downloadCsv()">
              @if (isExportingCsv) {
                <mat-spinner diameter="18"></mat-spinner>
              } @else {
                <mat-icon>table_view</mat-icon>
              }
              {{ 'QUERY_DOWNLOAD_CSV' | transloco }}
            </button>
          </span>
          <span [matTooltip]="isDeleted ? ('QUERY_DELETED_ACTION_TOOLTIP' | transloco) : ''">
            <button mat-stroked-button [disabled]="isExportingJson || !query || isDeleted" (click)="downloadJson()">
              @if (isExportingJson) {
                <mat-spinner diameter="18"></mat-spinner>
              } @else {
                <mat-icon>data_object</mat-icon>
              }
              {{ 'QUERY_DOWNLOAD_JSON' | transloco }}
            </button>
          </span>
          @if (isEditor) {
            <button
              mat-icon-button
              [disabled]="!result"
              [matTooltip]="'QUERY_SHOW_SQL' | transloco"
              (click)="showExecutedSql()"
            >
              <mat-icon>code</mat-icon>
            </button>
          }
          <span class="corner-divider"></span>
          <span [matTooltip]="isDeleted ? ('QUERY_DELETED_ACTION_TOOLTIP' | transloco) : ''">
            <button mat-flat-button color="primary" [disabled]="isExecuting || !query || isDeleted" (click)="execute()">
              @if (isExecuting) {
                <mat-spinner diameter="18"></mat-spinner>
              } @else {
                <mat-icon>play_arrow</mat-icon>
              }
              {{ 'QUERY_EXECUTE' | transloco }}
            </button>
          </span>
        </div>
      </div>

      <!-- Results -->
      @if (result) {
        <div class="results-header">
          {{ 'QUERY_RESULTS_COUNT' | transloco: { count: result.row_count, duration: result.duration_ms } }}
          @if (formattedBytesProcessed !== null) {
            — {{ formattedBytesProcessed }}
          }
        </div>

        <div class="results-grid-container">
          <birdz-table-toolbox [showSearchBar]="true" [showColumnsToolbox]="false" [gridApi]="gridApi" />
          <ag-grid-angular
            class="results-grid"
            [gridOptions]="gridOptions"
            [columnDefs]="agColumns"
            [rowData]="agRows"
            (gridReady)="onGridReady($event)"
          />
        </div>
      }

      <!-- Execution error -->
      @if (executionError) {
        <div class="execution-error">
          <div class="execution-error__message">
            <mat-icon>error_outline</mat-icon>
            <span>{{ executionError.message }}</span>
          </div>
          @if (executionError.sql) {
            <pre class="execution-error__sql">{{ executionError.sql }}</pre>
          }
        </div>
      }
    }
  </div>
</birdz-card>
